import { fitTrimToDuration, roundToFrame, validateTrim } from './time.ts';
import {
  EDIT_STATE_VERSION,
  MIN_TRIM_SECONDS,
  type ClipEdit,
  type ClipVersion,
  type PendingRework,
  type ProjectEditState,
  type ReconstructResult,
  type RenderClipInput,
  type RenderSnapshot,
  type SourceClipImage,
} from './types.ts';

function nowIso(): string {
  return new Date().toISOString();
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function newVersionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ver_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function sourceDurationOf(image: SourceClipImage): number {
  const pending = asNumber(image.pending_clip_duration, 0);
  const stored = asNumber(image.clip_duration, 0);
  return roundToFrame(Math.max(MIN_TRIM_SECONDS, stored || pending || 3));
}

function versionsFromImage(image: SourceClipImage): ClipVersion[] {
  const stored = Array.isArray(image.clip_versions) ? image.clip_versions : [];
  const mapped = stored
    .filter((row) => row && typeof row === 'object' && row.clipUrl)
    .map((row) => ({
      id: String(row.id || newVersionId()),
      clipUrl: String(row.clipUrl),
      clipPath: row.clipPath ?? null,
      duration: roundToFrame(asNumber(row.duration, sourceDurationOf(image))),
      createdAt: String(row.createdAt || nowIso()),
      instructions: row.instructions ?? null,
      generationId: row.generationId ?? null,
    }));

  if (image.clip_url) {
    const already = mapped.some((v) => v.clipUrl === image.clip_url);
    if (!already) {
      mapped.unshift({
        id: image.id,
        clipUrl: image.clip_url,
        clipPath: image.clip_path ?? null,
        duration: sourceDurationOf(image),
        createdAt: nowIso(),
        instructions: null,
        generationId: null,
      });
    }
  }
  return mapped;
}

export function createClipEditFromImage(image: SourceClipImage, order: number): ClipEdit | null {
  if (!image.clip_url) return null;
  const versions = versionsFromImage(image);
  const active = versions[0];
  if (!active) return null;
  const duration = active.duration;
  return {
    id: image.id,
    order,
    included: true,
    trimStart: 0,
    trimEnd: duration,
    sourceDuration: duration,
    activeVersionId: active.id,
    versions,
    pendingRework: null,
  };
}

export function defaultEditState(images: SourceClipImage[]): ReconstructResult {
  const clips: ClipEdit[] = [];
  const sorted = [...images].sort((a, b) => a.order_index - b.order_index);
  for (const image of sorted) {
    const clip = createClipEditFromImage(image, clips.length);
    if (clip) clips.push(clip);
  }
  if (!clips.length) {
    return {
      ok: false,
      reason: images.length ? 'no_clips' : 'final_only',
      message: images.length
        ? 'Clips are still generating. Editing will be available once each image has a video clip.'
        : 'This project only has a finished video, so individual clips cannot be edited.',
    };
  }
  return {
    ok: true,
    synthesized: true,
    state: {
      version: EDIT_STATE_VERSION,
      revision: 1,
      updatedAt: nowIso(),
      clips,
    },
  };
}

function parseClip(raw: unknown): ClipEdit | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const id = asString(obj.id);
  if (!id) return null;
  const versionsRaw = Array.isArray(obj.versions) ? obj.versions : [];
  const versions: ClipVersion[] = [];
  for (const row of versionsRaw) {
    const v = asObject(row);
    if (!v || !asString(v.clipUrl)) continue;
    versions.push({
      id: asString(v.id) || newVersionId(),
      clipUrl: String(v.clipUrl),
      clipPath: asString(v.clipPath),
      duration: roundToFrame(asNumber(v.duration, 3)),
      createdAt: asString(v.createdAt) || nowIso(),
      instructions: asString(v.instructions),
      generationId: asString(v.generationId),
    });
  }

  const sourceDuration = roundToFrame(asNumber(obj.sourceDuration, versions[0]?.duration || 3));
  const activeVersionId = asString(obj.activeVersionId) || versions[0]?.id || id;
  const pending = asObject(obj.pendingRework);
  let pendingRework: PendingRework | null = null;
  if (pending) {
    pendingRework = {
      generationId: asString(pending.generationId),
      instructions: asString(pending.instructions) || '',
      startedAt: asString(pending.startedAt) || nowIso(),
      status: (asString(pending.status) as PendingRework['status']) || 'processing',
      clipUrl: asString(pending.clipUrl),
      clipPath: asString(pending.clipPath),
      duration: pending.duration == null ? null : asNumber(pending.duration),
      error: asString(pending.error),
    };
  }

  const trimStart = roundToFrame(asNumber(obj.trimStart, 0));
  const trimEnd = roundToFrame(asNumber(obj.trimEnd, sourceDuration));
  const fitted = fitTrimToDuration(trimStart, trimEnd, sourceDuration);

  return {
    id,
    order: asNumber(obj.order, 0),
    included: obj.included !== false,
    trimStart: fitted.start,
    trimEnd: fitted.end,
    sourceDuration: fitted.sourceDuration,
    activeVersionId,
    versions,
    pendingRework,
  };
}

export function parseEditState(raw: unknown): ProjectEditState | null {
  const obj = asObject(raw);
  if (!obj) return null;
  const clips = Array.isArray(obj.clips)
    ? obj.clips.map(parseClip).filter((c): c is ClipEdit => !!c)
    : [];
  if (!clips.length) return null;
  return {
    version: EDIT_STATE_VERSION,
    revision: Math.max(1, Math.round(asNumber(obj.revision, 1))),
    updatedAt: asString(obj.updatedAt) || nowIso(),
    clips,
  };
}

export function reconstructEditState(
  images: SourceClipImage[],
  existing: unknown,
  options?: { hasFinalVideoOnly?: boolean },
): ReconstructResult {
  const usable = images.filter((img) => !!img.clip_url);
  if (!usable.length) {
    if (options?.hasFinalVideoOnly) {
      return {
        ok: false,
        reason: 'final_only',
        message:
          'This older project only has a finished video. Individual clips were not kept, so Review & edit is unavailable.',
      };
    }
    return defaultEditState(images);
  }

  const synthesized = defaultEditState(usable);
  if (!synthesized.ok) return synthesized;

  const parsed = parseEditState(existing);
  if (!parsed) return synthesized;

  const byId = new Map(parsed.clips.map((c) => [c.id, c]));
  const merged: ClipEdit[] = synthesized.state.clips.map((fresh, index) => {
    const prior = byId.get(fresh.id);
    if (!prior) return { ...fresh, order: index };
    const versions = fresh.versions.length ? fresh.versions : prior.versions;
    const active = versions.find((v) => v.id === prior.activeVersionId) ?? versions[0];
    const duration = active?.duration ?? fresh.sourceDuration;
    const fitted = fitTrimToDuration(prior.trimStart, prior.trimEnd, duration);
    return {
      ...prior,
      order: index,
      versions,
      activeVersionId: active?.id ?? fresh.activeVersionId,
      sourceDuration: duration,
      trimStart: fitted.start,
      trimEnd: fitted.end,
      pendingRework: prior.pendingRework ?? null,
    };
  });

  // Preserve user order when every current clip still exists in the saved edit.
  const savedOrder = parsed.clips
    .filter((c) => merged.some((m) => m.id === c.id))
    .sort((a, b) => a.order - b.order)
    .map((c) => c.id);
  if (savedOrder.length === merged.length) {
    merged.sort((a, b) => savedOrder.indexOf(a.id) - savedOrder.indexOf(b.id));
    merged.forEach((clip, i) => {
      clip.order = i;
    });
  }

  return {
    ok: true,
    synthesized: false,
    state: {
      version: EDIT_STATE_VERSION,
      revision: parsed.revision,
      updatedAt: parsed.updatedAt,
      clips: merged,
    },
  };
}

export function activeVersion(clip: ClipEdit): ClipVersion | null {
  return clip.versions.find((v) => v.id === clip.activeVersionId) ?? clip.versions[0] ?? null;
}

export function includedClips(state: ProjectEditState): ClipEdit[] {
  return [...state.clips].filter((c) => c.included && activeVersion(c)?.clipUrl).sort((a, b) => a.order - b.order);
}

export function selectedDuration(clip: ClipEdit): number {
  return roundToFrame(Math.max(0, clip.trimEnd - clip.trimStart));
}

export function totalEditedDuration(state: ProjectEditState): number {
  return roundToFrame(includedClips(state).reduce((sum, clip) => sum + selectedDuration(clip), 0));
}

export function validateEditState(state: ProjectEditState): { ok: true } | { ok: false; error: string } {
  if (!state.clips.length) return { ok: false, error: 'No clips in this edit' };
  const included = includedClips(state);
  if (!included.length) return { ok: false, error: 'Include at least one clip before rendering' };

  for (const clip of state.clips) {
    const version = activeVersion(clip);
    if (!version?.clipUrl && clip.included) {
      return { ok: false, error: `Clip ${clip.id} is missing a source video` };
    }
    const trim = validateTrim(clip.trimStart, clip.trimEnd, clip.sourceDuration);
    if (trim.ok === false) return { ok: false, error: trim.error };
  }
  return { ok: true };
}

export function setClipTrim(state: ProjectEditState, clipId: string, start: number, end: number): ProjectEditState {
  return {
    ...state,
    updatedAt: nowIso(),
    clips: state.clips.map((clip) => {
      if (clip.id !== clipId) return clip;
      const trim = validateTrim(start, end, clip.sourceDuration);
      if (!trim.ok) return clip;
      return { ...clip, trimStart: trim.trim.start, trimEnd: trim.trim.end };
    }),
  };
}

export function resetClipTrim(state: ProjectEditState, clipId: string): ProjectEditState {
  return {
    ...state,
    updatedAt: nowIso(),
    clips: state.clips.map((clip) =>
      clip.id === clipId
        ? { ...clip, trimStart: 0, trimEnd: clip.sourceDuration }
        : clip,
    ),
  };
}

export function setClipIncluded(state: ProjectEditState, clipId: string, included: boolean): ProjectEditState {
  return {
    ...state,
    updatedAt: nowIso(),
    clips: state.clips.map((clip) => (clip.id === clipId ? { ...clip, included } : clip)),
  };
}

export function moveClip(state: ProjectEditState, clipId: string, direction: -1 | 1): ProjectEditState {
  const ordered = [...state.clips].sort((a, b) => a.order - b.order);
  const index = ordered.findIndex((c) => c.id === clipId);
  const next = index + direction;
  if (index < 0 || next < 0 || next >= ordered.length) return state;
  const swapped = [...ordered];
  const current = swapped[index];
  swapped[index] = swapped[next];
  swapped[next] = current;
  return {
    ...state,
    updatedAt: nowIso(),
    clips: swapped.map((clip, order) => ({ ...clip, order })),
  };
}

export function reorderClips(state: ProjectEditState, orderedIds: string[]): ProjectEditState {
  const byId = new Map(state.clips.map((c) => [c.id, c]));
  const next: ClipEdit[] = [];
  for (const id of orderedIds) {
    const clip = byId.get(id);
    if (clip) next.push(clip);
  }
  for (const clip of state.clips) {
    if (!next.some((c) => c.id === clip.id)) next.push(clip);
  }
  return {
    ...state,
    updatedAt: nowIso(),
    clips: next.map((clip, order) => ({ ...clip, order })),
  };
}

export function attachPendingRework(
  state: ProjectEditState,
  clipId: string,
  pending: PendingRework,
): ProjectEditState {
  return {
    ...state,
    updatedAt: nowIso(),
    clips: state.clips.map((clip) => (clip.id === clipId ? { ...clip, pendingRework: pending } : clip)),
  };
}

export function acceptRework(state: ProjectEditState, clipId: string): {
  state: ProjectEditState;
  trimMessage?: string;
} {
  const clips = state.clips.map((clip) => {
    if (clip.id !== clipId || !clip.pendingRework?.clipUrl) return clip;
    const duration = roundToFrame(clip.pendingRework.duration || clip.sourceDuration);
    const version: ClipVersion = {
      id: newVersionId(),
      clipUrl: clip.pendingRework.clipUrl,
      clipPath: clip.pendingRework.clipPath ?? null,
      duration,
      createdAt: nowIso(),
      instructions: clip.pendingRework.instructions,
      generationId: clip.pendingRework.generationId,
    };
    const fitted = fitTrimToDuration(clip.trimStart, clip.trimEnd, duration);
    return {
      ...clip,
      versions: [version, ...clip.versions],
      activeVersionId: version.id,
      sourceDuration: duration,
      trimStart: fitted.start,
      trimEnd: fitted.end,
      pendingRework: null,
    };
  });
  const accepted = clips.find((c) => c.id === clipId);
  const fitted = accepted
    ? fitTrimToDuration(
        state.clips.find((c) => c.id === clipId)?.trimStart ?? 0,
        state.clips.find((c) => c.id === clipId)?.trimEnd ?? accepted.sourceDuration,
        accepted.sourceDuration,
      )
    : null;
  return {
    state: { ...state, updatedAt: nowIso(), clips },
    trimMessage: fitted?.message,
  };
}

export function rejectRework(state: ProjectEditState, clipId: string): ProjectEditState {
  return {
    ...state,
    updatedAt: nowIso(),
    clips: state.clips.map((clip) => (clip.id === clipId ? { ...clip, pendingRework: null } : clip)),
  };
}

export function restoreVersion(state: ProjectEditState, clipId: string, versionId: string): {
  state: ProjectEditState;
  trimMessage?: string;
} {
  const clips = state.clips.map((clip) => {
    if (clip.id !== clipId) return clip;
    const version = clip.versions.find((v) => v.id === versionId);
    if (!version) return clip;
    const fitted = fitTrimToDuration(clip.trimStart, clip.trimEnd, version.duration);
    return {
      ...clip,
      activeVersionId: version.id,
      sourceDuration: version.duration,
      trimStart: fitted.start,
      trimEnd: fitted.end,
    };
  });
  const next = clips.find((c) => c.id === clipId);
  const prev = state.clips.find((c) => c.id === clipId);
  const fitted = next && prev
    ? fitTrimToDuration(prev.trimStart, prev.trimEnd, next.sourceDuration)
    : null;
  return {
    state: { ...state, updatedAt: nowIso(), clips },
    trimMessage: fitted?.message,
  };
}

export function snapshotEdit(state: ProjectEditState): RenderSnapshot {
  const clips: RenderClipInput[] = includedClips(state).map((clip) => {
    const version = activeVersion(clip);
    return {
      id: clip.id,
      clipUrl: version?.clipUrl || '',
      clipPath: version?.clipPath ?? null,
      sourceDuration: clip.sourceDuration,
      trimStart: clip.trimStart,
      trimEnd: clip.trimEnd,
      order: clip.order,
      versionId: version?.id,
    };
  });
  return {
    revision: state.revision,
    capturedAt: nowIso(),
    clips,
  };
}

export function bumpRevision(state: ProjectEditState): ProjectEditState {
  return {
    ...state,
    revision: state.revision + 1,
    updatedAt: nowIso(),
  };
}

export function editIsDirtyVersusRender(
  state: ProjectEditState,
  lastRenderedRevision: number | null | undefined,
): boolean {
  if (lastRenderedRevision == null) return true;
  return state.revision !== lastRenderedRevision;
}
